import React, { useState, useMemo } from "react";
import { Box, Grid, Typography } from "@mui/material";
import FileCard from "./FileCard";
import ViewSizeSelector from "./ViewSizeSelector";
import useViewSize from "../hooks/useViewSize";
import FilePreviewUser from "./FilePreviewUser";
import ConfirmDialog from "../../../../shared/component/ConfirmDialog";
import FilterPanel from "./FilterPanel";
import useAllUserFiles from "../hooks/useUserFileHierarchy";

export default function AllFilesView({ files = [], loading, error }) {
  const [viewSize, setViewSize] = useState("medium");
  const { getGridSizes } = useViewSize(viewSize);
  const gridSizes = getGridSizes();

  // Filter state
  const [filters, setFilters] = useState({
    name: "",
    category: "",
    startDate: null,
    endDate: null,
  });

  // Flatten all files from all folders and subfolders
  const allFiles = files;

  // Apply filters to allFiles
  const filteredFiles = useMemo(() => {
    return allFiles.filter((file) => {
      // Filter by name (case-insensitive substring)
      if (
        filters.name &&
        !file.fileName.toLowerCase().includes(filters.name.toLowerCase())
      ) {
        return false;
      }
      // Filter by category
      if (filters.category && file.category !== filters.category) {
        return false;
      }
      // Filter by date range (createdAt or updatedAt)
      if (filters.startDate || filters.endDate) {
        const fileDate = file.createdAt
          ? new Date(file.createdAt.seconds * 1000)
          : file.updatedAt
          ? new Date(file.updatedAt.seconds * 1000)
          : null;
        if (fileDate) {
          if (
            filters.startDate &&
            fileDate < new Date(filters.startDate).setHours(0, 0, 0, 0)
          ) {
            return false;
          }
          if (
            filters.endDate &&
            fileDate > new Date(filters.endDate).setHours(23, 59, 59, 999)
          ) {
            return false;
          }
        }
      }
      return true;
    });
  }, [allFiles, filters]);

  // File preview states
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  // Delete confirmation dialog states
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  // Handlers
  const handlePreviewOpen = (file) => {
    setPreviewFile(file);
    setPreviewOpen(true);
  };
  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setPreviewFile(null);
  };

  const handleDeleteRequest = (file) => {
    setFileToDelete(file);
    setConfirmOpen(true);
  };
  const handleConfirmDelete = () => {
    // Implement delete logic or emit event to parent
    setConfirmOpen(false);
    setFileToDelete(null);
  };
  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setFileToDelete(null);
  };

  return (
    <Box>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}
      >
        <FilterPanel
          categories={[...new Set(allFiles.map((file) => file.category))]}
          onFilterChange={setFilters}
        />
        <ViewSizeSelector viewSize={viewSize} setViewSize={setViewSize} />
      </Box>
      <Grid container spacing={2}>
        {filteredFiles.map((file) => (
          <Grid item key={file.storagePath} {...gridSizes}>
            <FileCard
              file={file}
              onPreview={() => handlePreviewOpen(file)}
              onDelete={() => handleDeleteRequest(file)}
              viewSize={viewSize}
            />
          </Grid>
        ))}
      </Grid>
      <FilePreviewUser
        open={previewOpen}
        onClose={handlePreviewClose}
        file={previewFile}
      />
      <ConfirmDialog
        open={confirmOpen}
        title="Confirm Delete"
        message="Are you sure you want to delete this file? This action cannot be undone."
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </Box>
  );
}
